import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ListBuilder } from "@/integrations/aws/resources";
import { getInvoiceDownloadUrl, getMergedInvoicesUrl } from "@/integrations/aws/api";
import { liteList } from "@/lib/query-keys";
import { PageHeader } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchableSelect } from "@/components/searchable-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Plus,
  Layers,
  Download,
  FileDown,
  FileText,
  FilePen,
  CircleDollarSign,
  Loader2,
  Trash2,
} from "lucide-react";
import { StatCard } from "@/components/stat-card";
import { useConfirmDelete } from "@/components/confirm-dialog";
import { notify } from "@/lib/notify";
import { useAuth } from "@/lib/auth";
import {
  useRowSelection,
  BulkBar,
  SelectAllCheckbox,
  RowCheckbox,
} from "@/components/bulk-actions";
import {
  MultiDateRangeFilter,
  describeDateRange,
  EMPTY_DATE_RANGE,
  FilterSelect,
  isDateRangeSet,
  ListPage,
  ListPageHeader,
  ListPageTable,
  ListSearch,
  ListToolbar,
  SortHeader,
  TableMessageRow,
  TablePagination,
  useDebounced,
  useListSort,
  usePagination,
  type DateRange,
} from "@/components/list-controls";
import { ClientGroupSelect, CompanySelect } from "@/components/entity-select";
import { InvoiceExportDialog, type ExportScope } from "@/components/invoice-export-dialog";
import { money } from "@/lib/billing";
import { INVOICE_STATUS_LABEL, INVOICE_STATUS_TONE, invalidateInvoiceStamps } from "@/lib/invoices";
import { BulkInvoiceDialog } from "@/components/bulk-invoice-dialog";

const COMPANY_OPTIONS = liteList("companies_lite", "id, legal_name, client_group_id");
const GROUP_OPTIONS = liteList("client_groups_lite", "id, name");
const BANK_OPTIONS = liteList("bank_accounts_lite", "id, account_name, bank_name, active");

export const Route = createFileRoute("/_authenticated/invoices/")({
  head: () => ({
    meta: [
      { title: "Invoices — Somasi Ops" },
      {
        name: "description",
        content:
          "Create, track and export Somasi client invoices, including bulk invoicing of service bundles.",
      },
      { property: "og:title", content: "Invoices — Somasi Ops" },
      {
        property: "og:description",
        content:
          "Create, track and export Somasi client invoices, including bulk invoicing of service bundles.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: InvoicesPage,
});

type Invoice = {
  id: string;
  invoice_number: string;
  /** Null on a draft, which is dated when it is issued. */
  invoice_date: string | null;
  due_date: string | null;
  company_id: string | null;
  client_group_id: string | null;
  bank_account_id: string | null;
  total: number;
  status: "draft" | "sent" | "paid" | "partially_paid" | "cancelled";
  period_label: string | null;
};

function InvoicesPage() {
  const { canEdit } = useAuth();
  const qc = useQueryClient();
  const confirmDelete = useConfirmDelete();
  const [search, setSearch] = useState("");
  /** `outstanding` is the two unsettled statuses together, sent and partially paid. */
  const [status, setStatus] = useState<"all" | "outstanding" | Invoice["status"]>("all");
  // "none" is the invoices with no receiving account on them, which is what
  // someone chasing the unbanked ones is looking for.
  const [bank, setBank] = useState<"all" | "none" | string>("all");
  // Both date columns, each open at either end. `invoice_date` is when the
  // invoice was raised and `due_date` when it falls due, and the two questions
  // people ask of this list — "what did we bill in March" and "what is overdue"
  // — are one each, so they filter separately rather than sharing one range.
  const [raised, setRaised] = useState<DateRange>(EMPTY_DATE_RANGE);
  const [due, setDue] = useState<DateRange>(EMPTY_DATE_RANGE);
  // `""` for "any", which is what the pickers report when nothing is chosen.
  const [companyId, setCompanyId] = useState("");
  const [groupId, setGroupId] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const { data: companies = [] } = useQuery({
    queryKey: COMPANY_OPTIONS.key,
    queryFn: async () =>
      (await api.companies.list().all().select(COMPANY_OPTIONS.columns).order("legal_name")).data ??
      [],
  });

  const { data: groups = [] } = useQuery({
    queryKey: GROUP_OPTIONS.key,
    queryFn: async () =>
      (await api.clientGroups.list().all().select(GROUP_OPTIONS.columns).order("name")).data ?? [],
  });

  /**
   * The receiving accounts, for the Bank column and its filter.
   *
   * Unfiltered by `active` on purpose: an invoice raised last year may name an
   * account since marked inactive, and the column has to print its bank rather than a
   * dash. Listing it in the filter follows from the same thing — the invoices
   * banked into a closed account are exactly the ones worth looking up — so the
   * dropdown marks it instead of hiding it.
   *
   * Account numbers live on these rows and only the edit roles may read them,
   * so a viewer gets neither the column nor the filter.
   */
  const { data: banks = [] } = useQuery({
    // `/bank-accounts` is readable by the edit roles only — the same set
    // `canEdit` names — so a viewer never fires the request that would 403.
    enabled: canEdit,
    queryKey: BANK_OPTIONS.key,
    queryFn: async () =>
      (await api.bankAccounts.list().all().select(BANK_OPTIONS.columns).order("bank_name")).data ??
      [],
  });

  /**
   * The bank first, then the account it landed in.
   *
   * Which bank holds the money is what the column is read for, so it leads; the
   * account name follows because a bank may hold several and the two together
   * are the only unambiguous naming. Same order in the filter and its summary,
   * so what you picked is what the column echoes back.
   */
  const bankLabel = (bank: { account_name: string; bank_name: string }) => `${bank.bank_name}`;

  const bankedInto = (invoice: Pick<Invoice, "bank_account_id">) => {
    const bank = banks.find((b) => b.id === invoice.bank_account_id);
    return bank ? bankLabel(bank) : "—";
  };

  /**
   * Who the invoice is addressed to.
   *
   * Almost always a company, but an invoice may be raised to a client group as a
   * whole for work spread across its members, and that one stores no company —
   * so the group's name is the party rather than a dash.
   */
  const billedTo = (invoice: Pick<Invoice, "company_id" | "client_group_id">) =>
    companies.find((c) => c.id === invoice.company_id)?.legal_name ??
    (invoice.company_id ? null : groups.find((g) => g.id === invoice.client_group_id)?.name) ??
    "—";

  // Held back so a search is one request when typing stops, not one per key.
  const query = useDebounced(search.trim());
  /**
   * The date ranges are debounced for the same reason the search box is.
   * A `<input type="date">` fires a change the moment its three segments are all
   * filled, so typing the year of "2026-03-01" walks through 0002, 0020 and
   * 0202 on the way — three complete, wrong dates, and three requests — before
   * it reaches the one that was meant.
   */
  const raisedRange = useDebounced(raised);
  const dueRange = useDebounced(due);
  const filters = { query, status, bank, raisedRange, dueRange, companyId, groupId };
  const sort = useListSort({ column: "invoice_date", ascending: false });
  const pager = usePagination({ resetKey: { ...filters, sort: sort.sort } });
  const filtering =
    Boolean(query || companyId || groupId) ||
    status !== "all" ||
    bank !== "all" ||
    isDateRangeSet(raisedRange) ||
    isDateRangeSet(dueRange);

  /**
   * The page's filters, applied to any invoice query.
   *
   * Takes the builder rather than making one so the table and the export ask the
   * same question of two different endpoints — `GET /invoices` for the page,
   * `GET /invoices/export` for the file. Written out twice, the file would
   * eventually stop being the list it was taken from.
   *
   * An invoice stores `company_id` and not the company's name, so a search for
   * "Acme" cannot be an `ilike` on a column. The company list the page already
   * holds for the Company column answers it instead: the typed text picks out the
   * companies whose names match, and those ids go into the same OR group as the
   * invoice number. Filtering the fetched rows in the browser had the same
   * effect while the page held every invoice; it stops working the moment it
   * holds twenty of them.
   */
  const applyFilters = <T,>(list: ListBuilder<T>) => {
    if (status === "outstanding") list.in("status", ["sent", "partially_paid"]);
    else if (status !== "all") list.eq("status", status);
    if (bank === "none") list.is("bank_account_id", null);
    else if (bank !== "all") list.eq("bank_account_id", bank);
    if (companyId) list.eq("company_id", companyId);
    // An invoice carries its company's group, so this finds the group's
    // companies' invoices and the ones raised to the group itself alike.
    if (groupId) list.eq("client_group_id", groupId);

    // Both bounds inclusive, and either may stand alone. ISO dates are stored
    // as text and compare in order, so the bounds are the dates themselves.
    if (raisedRange.from) list.gte("invoice_date", raisedRange.from);
    if (raisedRange.to) list.lte("invoice_date", raisedRange.to);
    if (dueRange.from) list.gte("due_date", dueRange.from);
    if (dueRange.to) list.lte("due_date", dueRange.to);
    // `due_date` is nullable, and a missing date sorts below every real one —
    // so without this an invoice with no due date would answer "due on or
    // before March" as though it were due in the distant past. Asking anything
    // about the due date means asking about the invoices that have one.
    if (isDateRangeSet(dueRange)) list.not("due_date", "is", null);
    if (query) {
      const needle = query.toLowerCase();
      const companyIds = companies
        .filter((c) => (c.legal_name ?? "").toLowerCase().includes(needle))
        .map((c) => c.id);
      // The same trick for groups, so searching a group's name finds the
      // invoices raised to the group itself and not only to its companies.
      const groupIds = groups
        .filter((g) => (g.name ?? "").toLowerCase().includes(needle))
        .map((g) => g.id);
      list.or([
        { column: "invoice_number", op: "ilike", value: `%${query}%` },
        ...(companyIds.length
          ? [{ column: "company_id", op: "in" as const, value: companyIds }]
          : []),
        ...(groupIds.length
          ? [{ column: "client_group_id", op: "in" as const, value: groupIds }]
          : []),
      ]);
    }
    return list;
  };

  /** The list the filters describe — the table pages it, the export takes it whole. */
  const filteredList = () => {
    const list = api.invoices.list().order(sort.sort.column, { ascending: sort.sort.ascending });
    // Ties — a day's invoices, every draft — fall back to the number, newest
    // first, so a page break never lands somewhere different between requests.
    if (sort.sort.column !== "invoice_number") list.order("invoice_number", { ascending: false });
    return applyFilters(list);
  };

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "invoices",
      { ...filters, sort: sort.sort, page: pager.page, pageSize: pager.pageSize },
    ],
    queryFn: async () => {
      const { data, error, count } = await filteredList().page(pager.page, pager.pageSize);
      if (error) throw error;
      return { rows: (data ?? []) as Invoice[], total: count ?? 0 };
    },
    placeholderData: (previous) => previous,
  });

  const filtered = result?.rows ?? [];
  const total = result?.total;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["invoices"] });
    // The work log names the invoice each billed row went onto, status and all,
    // so a status changed here has to reach those cells too.
    invalidateInvoiceStamps(qc);
  };

  /**
   * The three tiles, which describe the register and not the page.
   *
   * They were sums over the rows the page had, which was every invoice. Now the
   * page has twenty, so the totals have to be asked for separately: two of them
   * are counts the API can answer with `head`, and the outstanding figure needs
   * the amounts, so it asks for the two unsettled statuses and their totals —
   * `select` keeps that to the two columns it adds up.
   */
  const { data: tiles } = useQuery({
    // Prefixed with "invoices" so the page's existing invalidation reaches it:
    // a status change or a delete moves these figures too.
    queryKey: ["invoices", "tiles"],
    queryFn: async () => {
      const [all, drafts, unsettled] = await Promise.all([
        api.invoices.list().countOnly(),
        api.invoices.list().eq("status", "draft").countOnly(),
        api.invoices.list().all().select("total").in("status", ["sent", "partially_paid"]),
      ]);
      const rows = (unsettled.data ?? []) as { total: number }[];
      return {
        invoices: all.count ?? 0,
        drafts: drafts.count ?? 0,
        outstanding: rows.reduce((sum, i) => sum + Number(i.total), 0),
      };
    },
  });

  /*
   * The summary cards are shortcuts: each one clears the other filters and
   * shows the invoices its figure counts, so the rows match the number.
   */
  const showInvoices = (next: typeof status) => {
    setSearch("");
    setBank("all");
    setRaised(EMPTY_DATE_RANGE);
    setDue(EMPTY_DATE_RANGE);
    setCompanyId("");
    setGroupId("");
    setStatus(next);
  };
  const onlyStatus =
    !search &&
    !companyId &&
    !groupId &&
    bank === "all" &&
    !isDateRangeSet(raised) &&
    !isDateRangeSet(due);

  // Ticks are kept across pages, so a merge can take more than the hundred rows
  // a page shows; a change of filter starts the selection again.
  const sel = useRowSelection(filtered, { acrossPages: true, resetKey: filters });

  /** Every invoice the filters match, in the table's order — ids only. */
  const matchingIds = async () => {
    const { data, error } = await filteredList().select("id").all();
    if (error) throw new Error(error.message);
    return ((data ?? []) as Pick<Invoice, "id">[]).map((i) => i.id);
  };

  const [selectingAll, setSelectingAll] = useState(false);
  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      sel.selectOnly(await matchingIds());
    } catch (error) {
      notify.error(error);
    } finally {
      setSelectingAll(false);
    }
  };

  /**
   * Which download is being drawn: an invoice's id for its row, `"selection"`
   * for the combined file. Drawing many can take a few seconds, so the button
   * says so instead of looking like the click was lost.
   */
  const [preparing, setPreparing] = useState<string | null>(null);

  const download = async (key: string, link: () => Promise<string>) => {
    setPreparing(key);
    try {
      window.location.assign(await link());
    } catch (error) {
      notify.error(error);
    } finally {
      setPreparing(null);
    }
  };

  /** The row's delete, asked the same way the invoice's own page asks it. */
  const removeInvoice = async (invoice: Invoice) => {
    if (
      !(await confirmDelete(
        `invoice ${invoice.invoice_number}`,
        "Any work and expenses on it become billable again.",
      ))
    ) {
      return;
    }
    // The server releases the work and expenses it billed as part of the delete.
    const { error } = await api.invoices.remove(invoice.id);
    if (error) return notify.error(error);
    notify.success("Invoice deleted");
    refresh();
  };

  /**
   * The selected invoices as one PDF, in the order the table shows them.
   *
   * One invoice goes through its own download instead, so it arrives under its
   * number and from the copy on file rather than as a one-page bundle.
   */
  const downloadSelected = () =>
    download("selection", async () => {
      // A selection reaching past this page is put in table order by asking for
      // the matching ids again; one inside it already has its order on screen.
      const onPage = filtered.filter((i) => sel.isSelected(i.id)).map((i) => i.id);
      const ids =
        onPage.length === sel.count
          ? onPage
          : (await matchingIds()).filter((id) => sel.isSelected(id));
      return ids.length === 1 ? getInvoiceDownloadUrl(ids[0]!) : getMergedInvoicesUrl(ids);
    });

  /**
   * The active filters in words, for the export dialog and the PDF's header.
   *
   * Null when nothing is filtered, which is what tells the dialog that "filtered"
   * and "the whole register" are the same set — and which of the two to offer
   * first.
   */
  const filterSummary =
    [
      query ? `matching “${query}”` : null,
      status !== "all" ? `status ${status.replace("_", " ")}` : null,
      bank === "none"
        ? "with no receiving account"
        : bank !== "all"
          ? (() => {
              const into = banks.find((b) => b.id === bank);
              return `banked into ${into ? bankLabel(into) : "an account"}`;
            })()
          : null,
      companyId
        ? `for ${companies.find((c) => c.id === companyId)?.legal_name ?? "one company"}`
        : null,
      groupId ? `in ${groups.find((g) => g.id === groupId)?.name ?? "one client group"}` : null,
      describeDateRange("dated", raisedRange),
      describeDateRange("due", dueRange),
    ]
      .filter(Boolean)
      .join(", ") || null;

  /**
   * The rows behind whichever file was asked for.
   *
   * One request to `GET /invoices/export`, which joins the lines and resolves the
   * company, group and catalogue names server-side. `all()` because a list
   * endpoint answers with one page when no limit is sent, and a file that stopped
   * at the first ten invoices is the whole hazard here.
   */
  const fetchExportRows = async (scope: ExportScope) => {
    const list = api.invoices
      .exportLines()
      .order("invoice_date", { ascending: false })
      .order("invoice_number", { ascending: false });
    const { data, error } = await (scope === "filtered" ? applyFilters(list) : list).all();
    if (error) throw new Error(error.message);
    return data ?? [];
  };

  return (
    <ListPage>
      <ListPageHeader>
        <PageHeader
          title="Invoices"
          description="Every invoice starts pre-filled with the client's fees and their unbilled work and expenses."
          actions={
            <>
              <Button variant="outline" onClick={() => setExportOpen(true)}>
                <Download className="mr-2 h-4 w-4" /> Export
              </Button>
              {canEdit && (
                <Button variant="outline" onClick={() => setBulkOpen(true)}>
                  <Layers className="mr-2 h-4 w-4" /> Bulk invoice
                </Button>
              )}
              {canEdit && (
                <Button asChild>
                  <Link to="/invoices/new">
                    <Plus className="mr-2 h-4 w-4" /> New invoice
                  </Link>
                </Button>
              )}
            </>
          }
        />

        {/* Each card is a shortcut: it shows the invoices behind its figure. */}
        <div className="mb-4 grid gap-3 sm:grid-cols-3">
          <StatCard
            label="Invoices"
            icon={FileText}
            value={tiles ? String(tiles.invoices) : "—"}
            onClick={() => showInvoices("all")}
            active={onlyStatus && status === "all"}
          />
          <StatCard
            label="Drafts"
            icon={FilePen}
            value={tiles ? String(tiles.drafts) : "—"}
            tone={tiles?.drafts ? "warn" : "ok"}
            onClick={() => showInvoices("draft")}
            active={onlyStatus && status === "draft"}
          />
          <StatCard
            label="Outstanding"
            icon={CircleDollarSign}
            value={tiles ? money(tiles.outstanding) : "—"}
            hint="Sent or partially paid"
            tone={tiles?.outstanding ? "warn" : "ok"}
            onClick={() => showInvoices("outstanding")}
            active={onlyStatus && status === "outstanding"}
          />
        </div>

        <ListToolbar>
          <ListSearch
            value={search}
            onChange={setSearch}
            placeholder="Search invoice number or company…"
          />
          <FilterSelect
            value={status}
            onChange={(v) => setStatus(v as typeof status)}
            options={[
              { value: "all", label: "All statuses" },
              { value: "outstanding", label: "Outstanding" },
              { value: "draft", label: "Draft" },
              { value: "sent", label: "Sent" },
              { value: "partially_paid", label: "Partially paid" },
              { value: "paid", label: "Paid" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
          {canEdit && (
            <SearchableSelect
              value={bank}
              onChange={setBank}
              placeholderValue="all"
              options={[
                { value: "all", label: "All banks" },
                { value: "none", label: "No bank account" },
                ...banks.map((b) => ({
                  value: b.id,
                  label: `${b.bank_name}${b.active ? "" : " (inactive)"}`,
                  hint: b.account_name,
                })),
              ]}
              searchPlaceholder="Search banks or accounts…"
              emptyText="No bank or account matches that."
              className="w-56"
              contentClassName="w-80 p-0"
            />
          )}
          {/* One button, a tab per date; each keeps its own range and they combine. */}
          <MultiDateRangeFilter
            fields={[
              { key: "invoice_date", label: "Invoice date" },
              { key: "due_date", label: "Due date" },
            ]}
            value={{ invoice_date: raised, due_date: due }}
            onChange={(next) => {
              setRaised(next.invoice_date ?? EMPTY_DATE_RANGE);
              setDue(next.due_date ?? EMPTY_DATE_RANGE);
            }}
            className="w-64"
          />
          <ClientGroupSelect
            value={groupId}
            onChange={(next) => {
              setGroupId(next);
              // A company outside the newly chosen group would AND with it to
              // nothing, and a filter bar that silently describes an empty set
              // is worse than one that drops the narrower choice.
              setCompanyId("");
            }}
            clearable
            clearLabel="Any client group"
            placeholder="Any client group"
            className="w-48"
          />
          <CompanySelect
            value={companyId}
            onChange={setCompanyId}
            // Narrowed by the group above, so the two filters read as one
            // question rather than two that can contradict each other.
            filters={groupId ? { clientGroupId: groupId } : {}}
            showClientGroup={!groupId}
            clearable
            clearLabel="Any company"
            placeholder="Any company"
            className="w-56"
          />
        </ListToolbar>

        {canEdit && (
          <BulkBar
            sel={sel}
            resource={api.invoices}
            entityLabel="invoice"
            onDone={refresh}
            actions={
              <Button
                size="sm"
                variant="outline"
                disabled={preparing === "selection"}
                onClick={() => void downloadSelected()}
              >
                {preparing === "selection" ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FileDown className="mr-2 h-4 w-4" />
                )}
                {preparing === "selection"
                  ? "Preparing PDF…"
                  : sel.count === 1
                    ? "Download PDF"
                    : "Download merged PDF"}
              </Button>
            }
          />
        )}
        {canEdit && total !== undefined && total > filtered.length && sel.allSelected && (
          <p className="mb-3 text-sm text-muted-foreground">
            {sel.count >= total ? (
              <>
                All {total} invoices matching these filters are selected.{" "}
                <Button variant="link" className="h-auto p-0" onClick={() => sel.clear()}>
                  Clear selection
                </Button>
              </>
            ) : (
              <>
                {sel.count === filtered.length
                  ? `All ${filtered.length} invoices on this page are selected.`
                  : `${sel.count} invoices selected, including every one on this page.`}{" "}
                <Button
                  variant="link"
                  className="h-auto p-0"
                  disabled={selectingAll}
                  onClick={() => void selectAllMatching()}
                >
                  {selectingAll ? "Selecting…" : `Select all ${total} matching invoices`}
                </Button>
              </>
            )}
          </p>
        )}
      </ListPageHeader>

      <ListPageTable>
        <Table containerClassName="min-h-0 flex-1">
          <TableHeader sticky>
            <TableRow>
              {canEdit && (
                <TableHead className="w-10">
                  <SelectAllCheckbox sel={sel} />
                </TableHead>
              )}
              <SortHeader sort={sort} column="invoice_number">
                Number
              </SortHeader>
              <SortHeader sort={sort} column="invoice_date">
                Date
              </SortHeader>
              {/* Resolved from the company and group lists, not a column of the
                  invoice, so there is nothing for the server to order by. */}
              <TableHead>Company</TableHead>
              <SortHeader sort={sort} column="period_label">
                Period
              </SortHeader>
              <SortHeader sort={sort} column="due_date">
                Due
              </SortHeader>
              {canEdit && <TableHead>Bank received</TableHead>}
              <SortHeader sort={sort} column="total" className="text-right" align="right">
                Total
              </SortHeader>
              <SortHeader sort={sort} column="status">
                Status
              </SortHeader>
              {/* The download endpoint is for the edit roles, like the bank. */}
              {canEdit && <TableHead className="w-24 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableMessageRow colSpan={canEdit ? 10 : 7}>Loading…</TableMessageRow>
            ) : filtered.length === 0 ? (
              <TableMessageRow colSpan={canEdit ? 10 : 7}>
                {filtering ? "No invoices match these filters." : "No invoices yet."}
              </TableMessageRow>
            ) : (
              filtered.map((i) => (
                <TableRow key={i.id} data-state={sel.isSelected(i.id) ? "selected" : undefined}>
                  {canEdit && (
                    <TableCell>
                      <RowCheckbox sel={sel} id={i.id} />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">
                    <Link
                      to="/invoices/$invoiceId"
                      params={{ invoiceId: i.id }}
                      className="text-primary hover:underline"
                    >
                      {i.invoice_number}
                    </Link>
                  </TableCell>
                  {/* A draft has not been raised, so it has no date to show —
                      and the status column beside it says which it is. */}
                  <TableCell>{i.invoice_date ?? "—"}</TableCell>
                  <TableCell>{billedTo(i)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {i.period_label ?? "—"}
                  </TableCell>
                  <TableCell>{i.due_date ?? "—"}</TableCell>
                  {canEdit && (
                    <TableCell className="text-sm text-muted-foreground">{bankedInto(i)}</TableCell>
                  )}
                  <TableCell className="text-right font-medium">{money(Number(i.total))}</TableCell>
                  <TableCell>
                    <Badge className={INVOICE_STATUS_TONE[i.status]}>
                      {INVOICE_STATUS_LABEL[i.status]}
                    </Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="whitespace-nowrap text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label={`Download ${i.invoice_number} as PDF`}
                        title="Download PDF"
                        disabled={preparing === i.id}
                        onClick={() => void download(i.id, () => getInvoiceDownloadUrl(i.id))}
                      >
                        {preparing === i.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <FileDown className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={`Delete ${i.invoice_number}`}
                        title="Delete invoice"
                        onClick={() => void removeInvoice(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination pagination={pager} total={total} loading={isLoading} unit="invoice" />
      </ListPageTable>

      <BulkInvoiceDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={refresh} />

      <InvoiceExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        filteredCount={total}
        totalCount={tiles?.invoices}
        filterSummary={filterSummary}
        // Account names are readable by the edit roles only, so a viewer gets the
        // detail workbook without a Bank column rather than an empty one.
        withBank={canEdit}
        fetchRows={fetchExportRows}
      />
    </ListPage>
  );
}
